import { useState } from 'react';
import { Calendar, ArrowRight, Tag } from 'lucide-react';
import Seo from '../components/Seo';

const UpdatesPage = () => {
  const [selectedTag, setSelectedTag] = useState('all');

  const updates = [
    {
      id: 1,
      title: "PLATFORM LAUNCH ANNOUNCED",
      date: "2026-05-20",
      tag: "announcement",
      excerpt: "We're thrilled to announce that Unight has officially launched today, May 20th, 2026! Welcome to the UK & India's premier underground nightlife platform — the floor is yours.",
      content: "After months of development and community feedback, Unight is live. Our platform reimagines how you discover underground events, connect with artists, and experience nightlife culture. From this day on, expect regular drops — venue partnerships, artist spotlights, ticket releases and more.",
      featured: true
    },
    {
      id: 2,
      title: "VERIFIED ARTIST PROFILES",
      date: "2026-05-20",
      tag: "feature",
      excerpt: "Introducing verified artist profiles — ensuring authenticity and trust in the underground scene. Real artists, real music, real experiences.",
      content: "Our verification system helps you discover genuine underground artists while protecting the community from imposters. Each verified profile has been manually reviewed by our team."
    },
    {
      id: 3,
      title: "SECURE TICKETING SYSTEM",
      date: "2026-05-20",
      tag: "feature",
      excerpt: "Our integrated ticketing system with Razorpay ensures safe, seamless transactions. No more sketchy ticket resales or fake entries.",
      content: "We've partnered with Razorpay to bring you a secure, reliable ticketing experience. Every ticket is verified and linked to your account, eliminating fraud and ensuring you always get what you paid for."
    },
    {
      id: 4,
      title: "COMMUNITY-FIRST APPROACH",
      date: "2026-05-20",
      tag: "vision",
      excerpt: "At Unight, the community comes first. We're building tools that empower artists, promoters, and ravers alike.",
      content: "Our platform is designed with input from the community. From event discovery to artist promotion, every feature is crafted to serve the underground scene."
    },
    {
      id: 5,
      title: "EXPANSION TO 9 CITIES",
      date: "2026-05-20",
      tag: "roadmap",
      excerpt: "Unight is rolling out across major cities in the UK and India — London, Manchester, Mumbai, Delhi, Bangalore and more.",
      content: "We're committed to bringing the underground scene to every corner of the UK and India. Our city-specific event discovery will help you find the best local events wherever you are."
    },
    {
      id: 6,
      title: "BETA TESTERS WANTED",
      date: "2026-05-20",
      tag: "announcement",
      excerpt: "Join our exclusive beta program and help shape the future of Unight. Early access, founding member perks, and your voice in our development.",
      content: "We're looking for passionate music lovers to join our beta testing program. Help us refine the platform and earn exclusive founding member benefits."
    }
  ];

  const tags = [
    { id: 'all', label: 'ALL' },
    { id: 'announcement', label: 'ANNOUNCEMENTS' },
    { id: 'feature', label: 'FEATURES' },
    { id: 'roadmap', label: 'ROADMAP' },
    { id: 'vision', label: 'VISION' }
  ];

  const filteredUpdates = selectedTag === 'all' 
    ? updates 
    : updates.filter(u => u.tag === selectedTag);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).toUpperCase();
  };

  const getTagColor = (tag) => {
    const colors = {
      announcement: 'bg-[#B5FF00] text-black',
      feature: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
      roadmap: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
      vision: 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
    };
    return colors[tag] || 'bg-zinc-800 text-zinc-400';
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="updates-page">
      <Seo title="Updates & Announcements" description="Latest news from Unight — new city launches, product updates, curated events, and stories from the underground." path="/updates" />
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/30 to-transparent" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <p className="text-zinc-500 text-lg italic mb-4 opacity-0 animate-fade-in" 
             style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
            Stay in the loop
          </p>
          
          <h1 
            className="font-['Josefin_Sans'] text-5xl md:text-6xl lg:text-7xl font-bold italic text-white mb-6 opacity-0 animate-fade-in-up tracking-tight"
            style={{ animationDelay: '0.2s', animationFillMode: 'forwards', letterSpacing: '-0.059em' }}
            data-testid="updates-title"
          >
            PLATFORM UPDATES
          </h1>
          
          <p className="text-zinc-400 max-w-2xl opacity-0 animate-fade-in-up"
             style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
            The latest news, features, and announcements from Unight. Follow our journey as we build the future of underground nightlife discovery.
          </p>
        </div>
      </section>

      {/* Filter Tags */}
      <section className="py-8 border-b border-zinc-800">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-wrap gap-3">
            {tags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => setSelectedTag(tag.id)}
                className={`px-4 py-2 font-mono text-xs tracking-widest transition-all ${
                  selectedTag === tag.id
                    ? 'bg-[#B5FF00] text-black'
                    : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-700'
                }`}
                data-testid={`filter-${tag.id}`}
              >
                {tag.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Updates Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUpdates.map((update, index) => (
              <article
                key={update.id}
                className={`bg-[#0A0A0A] border border-zinc-800 hover:border-zinc-700 transition-all opacity-0 animate-fade-in-up group ${
                  update.featured ? 'md:col-span-2 lg:col-span-2' : ''
                }`}
                style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
                data-testid={`update-${update.id}`}
              >
                <div className="p-6 md:p-8">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-3 py-1 text-xs font-mono tracking-wider ${getTagColor(update.tag)}`}>
                      {update.tag.toUpperCase()}
                    </span>
                    <div className="flex items-center gap-2 text-zinc-500 text-sm">
                      <Calendar size={14} />
                      <span className="font-mono text-xs">{formatDate(update.date)}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className={`font-['Josefin_Sans'] font-semibold text-white mb-4 group-hover:text-[#B5FF00] transition-colors tracking-tight ${
                    update.featured ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'
                  }`} style={{ letterSpacing: '-0.059em' }}>
                    {update.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-zinc-400 mb-6 leading-relaxed">
                    {update.excerpt}
                  </p>

                  {/* Read More */}
                  <button className="flex items-center gap-2 text-[#B5FF00] font-mono text-sm tracking-wider group-hover:gap-3 transition-all">
                    READ MORE
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 border-t border-zinc-800">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="font-['Josefin_Sans'] text-3xl md:text-4xl font-semibold text-white mb-4 tracking-tight" 
              style={{ letterSpacing: '-0.059em' }}>
            DON'T MISS AN UPDATE
          </h2>
          <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
            Join our waitlist to receive the latest news, feature announcements, and exclusive early access to Unight.
          </p>
          <a 
            href="/waitlist"
            className="btn-primary inline-flex items-center gap-2"
          >
            JOIN THE WAITLIST
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span className="footer-brand">UNIGHT</span>
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_venue-pulse-14/artifacts/8f7het1z_Unight%202d%20Logos%20%28TM%29-05.png"
              alt="Unight"
              className="h-8"
            />
            <span className="font-mono text-xs text-zinc-600 tracking-widest">
              © 2025 UNIGHT. ALL RIGHTS RESERVED.
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UpdatesPage;
